import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Button } from 'antd';
import { DotLoading, List } from 'antd-mobile';
import { throttle, uniqBy } from 'lodash-es';
import { klona } from 'klona';
import { useSize } from 'ahooks';
import { useLocation } from 'react-router';
import noMemberImg from '@/assets/image/no-member.png';
import useNav from '@/hooks/useNav';
import { Header, ScrollView } from '@/components';
import service from '@/service';
import useDebounce from '@/hooks/useDebounce';
import { JUDGE_AS_VERTICAL_SCREEN_HEIGHT } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import cx from './index.module.scss';
import MemberItem from './member-item';
import ScreenFlipTip from './screen-flip-tip';

interface Props {}
const INIT_LOAD_SIZE = 12;

const SearchMember: React.FC<Props> = () => {
  const [memberList, setMemberList] = useState<GetCustomersResponse>([]);
  const [inputVal, setInputVal] = useState('');
  const [isTipOpen, setIsTipOpen] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const inputRef = useRef(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const location = useLocation();

  const sizeRef = useRef(null);
  const screenSize = useSize(sizeRef);

  const nav = useNav();

  const searchMemberByWords =
    // eslint-disable-next-line fuxi/max-params-with-fixable
    async (pageSize: number, cursor: number, words?: string, reset: boolean = false) => {
      // if (!words.trim()) return;

      setIsLoading(true);
      try {
        const { data } = await service.customer.getCustomers({
          pageSize,
          cursor,
          searchWords: !!words ? encodeURIComponent(words) : undefined,
        });
        setIsSearched(true);
        reset
          ? setMemberList(data)
          : setMemberList(preValue => {
              const clonedValue = klona(preValue);
              const newMemberList = [...clonedValue, ...data];
              return uniqBy(newMemberList, 'customerId');
            });
        const _hasMoreData = data?.length >= pageSize;
        setHasMoreData(_hasMoreData);
        if (_hasMoreData) {
          scrollViewRef.current?.bsInstance?.finishPullUp();
        }
      } finally {
        setIsLoading(false);
      }
    };

  useDebounce(
    () => {
      if (!inputVal) {
        setMemberList([]);
        setIsSearched(false);
      }
      searchMemberByWords(INIT_LOAD_SIZE, 0, inputVal, true);
    },
    [inputVal],
    600
  );

  const loadMore = useMemo(
    () =>
      throttle(async () => {
        searchMemberByWords(6, memberList?.length, inputVal);
      }, 600),
    [inputVal, memberList]
  );

  useEffect(() => {
    searchMemberByWords(INIT_LOAD_SIZE, 0, null, true);
  }, []);

  return (
    <div className={cx('search-member')} ref={sizeRef}>
      <div className={cx('searched')}>
        <Header title="搜索会员" backConfig={{ text: '首页', action: () => nav('/', { replace: true }) }} />
        <div className={cx('input-wrapper')}>
          <Input
            ref={inputRef}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            className={cx('input')}
            placeholder="请输入手机尾号或会员名进行搜索"
            enterKeyHint="search"
            allowClear
          />
        </div>
        <>
          {memberList?.length ? (
            <ScrollView autoRefresh className={cx('list-wrapper')} ref={scrollViewRef} onPullingUp={loadMore}>
              <List>
                <div className={cx('member-list')}>
                  {memberList.map(
                    item =>
                      item && (
                        <MemberItem
                          key={item.customerId}
                          isTipOpen={isTipOpen}
                          setIsTipOpen={setIsTipOpen}
                          isVerticalScreen={screenSize?.height > JUDGE_AS_VERTICAL_SCREEN_HEIGHT}
                          {...item}
                        />
                      )
                  )}
                </div>
                <div className={cx('load-data-info')}>{hasMoreData ? '正在加载更多...' : '没有更多了'}</div>
              </List>
            </ScrollView>
          ) : isLoading ? (
            <div className={cx('loading-wrapper')}>
              <DotLoading />
              正在加载数据
            </div>
          ) : (
            isSearched && (
              <div className={cx('no-member')}>
                <img src={noMemberImg} className={cx('no-member-img')} alt="no-member" />
                <div className={cx('text')}>未搜索到符合要求的会员，请添加或重新搜索</div>
                <Button
                  type="primary"
                  className={cx('btn')}
                  onClick={() => nav('/user/add-member', { state: location?.state })}
                >
                  <div className={cx('btn-wrapper')}>
                    <span>添加会员</span>
                  </div>
                </Button>
              </div>
            )
          )}
        </>
      </div>
      <ScreenFlipTip open={isTipOpen} setOpen={setIsTipOpen} />
    </div>
  );
};

export default SearchMember;
